import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Paperclip, ImageOff } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { toast } from 'sonner'
import { useUIv2 } from '@/hooks/useUIv2'
import BugReportModalLegacy from './BugReportModalLegacy'

const MAX_ATTACHMENTS = 5
const MAX_SINGLE_FILE_BYTES = 20 * 1024 * 1024 // 20MB
const MAX_CHARS = 2000

export interface BugReportModalSubmitData {
  name?: string
  email?: string
  message: string
  attachments: File[]
}

interface BugReportModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: BugReportModalSubmitData) => Promise<void>
  defaultName?: string
  defaultEmail?: string
  screenshotDataUrl?: string | null
  onRemoveScreenshot?: () => void
  /** Annotators: suppress the screenshot preview/lightbox (still attached to the report). */
  hideScreenshotPreview?: boolean
  screenshotHiddenLabel?: string
  title: string
  subtitle?: string
  labelName: string
  labelEmail: string
  labelComments: string
  placeholderComments?: string
  labelAttachments: string
  labelAttachMedia: string
  getTooManyAttachmentsError: (max: number) => string
  getFileTooLargeError: (fileName: string, maxMb: number) => string
  labelClose: string
  labelSubmit: string
  labelSubmitting: string
  isLoading?: boolean
}

interface State {
  name: string
  email: string
  message: string
  attachments: File[]
  isSubmitting: boolean
}

type Action =
  | { type: 'SET_NAME'; value: string }
  | { type: 'SET_EMAIL'; value: string }
  | { type: 'SET_MESSAGE'; value: string }
  | { type: 'SET_ATTACHMENTS'; value: File[] }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'RESET'; name: string; email: string }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.value }
    case 'SET_EMAIL':
      return { ...state, email: action.value }
    case 'SET_MESSAGE':
      return { ...state, message: action.value }
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.value }
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value }
    case 'RESET':
      return {
        name: action.name,
        email: action.email,
        message: '',
        attachments: [],
        isSubmitting: false,
      }
  }
}

const BugReportModalV2 = ({
  isOpen,
  onClose,
  onSubmit,
  defaultName,
  defaultEmail,
  screenshotDataUrl,
  onRemoveScreenshot,
  hideScreenshotPreview = false,
  screenshotHiddenLabel = 'Screenshot attached to your report',
  title,
  subtitle,
  placeholderComments,
  labelAttachMedia,
  getTooManyAttachmentsError,
  getFileTooLargeError,
  labelSubmit,
  labelSubmitting,
  isLoading = false,
}: BugReportModalProps) => {
  const [state, dispatch] = useReducer(reducer, {
    name: defaultName || '',
    email: defaultEmail || '',
    message: '',
    attachments: [],
    isSubmitting: false,
  })

  const anyLoading = isLoading || state.isSubmitting
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [screenshotPreviewOpen, setScreenshotPreviewOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setScreenshotPreviewOpen(false)
    dispatch({ type: 'RESET', name: defaultName || '', email: defaultEmail || '' })
  }, [isOpen, defaultName, defaultEmail])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (screenshotPreviewOpen) {
          setScreenshotPreviewOpen(false)
        } else {
          handleClose()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files)
      if (incoming.length === 0) return
      const next: File[] = [...state.attachments]
      for (const file of incoming) {
        if (next.length >= MAX_ATTACHMENTS) {
          toast.error(getTooManyAttachmentsError(MAX_ATTACHMENTS))
          break
        }
        if (file.size > MAX_SINGLE_FILE_BYTES) {
          toast.error(getFileTooLargeError(file.name, 20))
          continue
        }
        const dup = next.some(
          (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
        )
        if (!dup) next.push(file)
      }
      dispatch({ type: 'SET_ATTACHMENTS', value: next })
    },
    [state.attachments, getTooManyAttachmentsError, getFileTooLargeError]
  )

  const removeAttachmentAt = (idx: number) => {
    dispatch({ type: 'SET_ATTACHMENTS', value: state.attachments.filter((_, i) => i !== idx) })
  }

  const handleClose = () => {
    if (anyLoading) return
    onClose()
  }

  const handleSubmit = async () => {
    if (anyLoading) return
    if (!state.message.trim()) return
    dispatch({ type: 'SET_SUBMITTING', value: true })
    try {
      const allAttachments = [...state.attachments]
      if (screenshotDataUrl) {
        const res = await fetch(screenshotDataUrl)
        const blob = await res.blob()
        allAttachments.unshift(new File([blob], `screenshot-${Date.now()}.jpg`, { type: 'image/jpeg' }))
      }
      await onSubmit({
        name: state.name.trim() || undefined,
        email: state.email.trim() || undefined,
        message: state.message.trim(),
        attachments: allAttachments,
      })
      onClose()
    } catch (err) {
      console.error('Failed to submit bug report', err)
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false })
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      role="presentation"
      // TOP LAYER, above every board overlay — the card preview's backdrop and the
      // in-app meeting panel sit at 9999, the board tour at 9998. This is the
      // surface for reporting that something on screen is broken, so it has to
      // open over whatever is on screen; at z-1000 it opened BEHIND an open card
      // preview, which made the bug button look dead exactly when a user had a
      // bug in front of them. It is a true modal and closing it returns to
      // whatever was underneath, so sitting on top costs nothing.
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000]"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        className="relative flex w-[684px] flex-col overflow-clip rounded-xl border border-[#e5e5e5] bg-white shadow-lg dark:border-dark-border dark:bg-dark-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={anyLoading}
          className="absolute right-4 top-4 size-4 opacity-70 hover:opacity-100 transition-opacity disabled:opacity-30"
        >
          <X className="size-4" />
        </button>

        {/* Header */}
        <div className="flex flex-col gap-2 p-4">
          <h3 id="bug-report-title" className="text-base font-medium text-foreground">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2 px-4 pb-4">
          {/* Textarea container — screenshot thumbnails go inside */}
          <div className="flex h-[204px] flex-col rounded-lg border border-input bg-white p-3 dark:bg-dark-card focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/50">
            <textarea
              className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              placeholder={placeholderComments || 'Tell us more about the issue you encountered'}
              value={state.message}
              maxLength={MAX_CHARS}
              onChange={(e) => dispatch({ type: 'SET_MESSAGE', value: e.target.value })}
              disabled={anyLoading}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit()
              }}
            />

            {/* Screenshot + attachment thumbnails inside the textarea area */}
            <div className="flex flex-wrap gap-2 mt-1">
              {screenshotDataUrl && (hideScreenshotPreview ? (
                // Annotator: no reachable full-res <img> (no right-click-save / lightbox);
                // the screenshot is still attached to the submitted report.
                <div
                  data-testid="bug-report-screenshot-hidden"
                  className="flex h-[90px] w-36 flex-col items-center justify-center gap-1 rounded border border-input bg-muted p-2 text-center"
                >
                  <ImageOff className="size-5 text-muted-foreground" />
                  <span className="text-xs leading-tight text-muted-foreground">{screenshotHiddenLabel}</span>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={screenshotDataUrl}
                    alt="Screenshot"
                    role="button"
                    tabIndex={0}
                    className="h-[90px] w-36 rounded border border-[#0e7490] object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setScreenshotPreviewOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setScreenshotPreviewOpen(true) }
                    }}
                  />
                  <button
                    type="button"
                    className="absolute -top-1.5 -right-1.5 flex size-6 items-center justify-center rounded-sm bg-[#0e7490] text-white hover:bg-[#0e7490]/80 transition-colors disabled:opacity-50"
                    onClick={onRemoveScreenshot}
                    disabled={anyLoading}
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              {state.attachments.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="relative flex h-[90px] w-36 items-center justify-center rounded border border-input bg-muted p-2">
                  <span className="text-xs text-muted-foreground truncate">{file.name}</span>
                  <button
                    type="button"
                    className="absolute -top-1.5 -right-1.5 flex size-6 items-center justify-center rounded-sm bg-[#0e7490] text-white hover:bg-[#0e7490]/80 transition-colors disabled:opacity-50"
                    onClick={() => removeAttachmentAt(idx)}
                    disabled={anyLoading}
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Character counter */}
          <p className="text-sm text-muted-foreground text-right">
            {state.message.length} / {MAX_CHARS} characters used
          </p>
        </div>

        {/* Screenshot lightbox */}
        {screenshotPreviewOpen && screenshotDataUrl && !hideScreenshotPreview && createPortal(
          <div
            role="presentation"
            // Above the report form itself, which is now the top layer (see above).
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10100] cursor-pointer"
            onClick={() => setScreenshotPreviewOpen(false)}
          >
            <img
              src={screenshotDataUrl}
              alt="Screenshot preview"
              className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[#e5e5e5] bg-muted/50 p-4 dark:border-dark-border">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }}
            />
            <IntuiButton
              variant="outline"
              leftIcon={<Paperclip />}
              disabled={anyLoading || state.attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              {labelAttachMedia}
            </IntuiButton>
          </div>
          <IntuiButton
            onClick={handleSubmit}
            disabled={!state.message.trim() || anyLoading}
          >
            {anyLoading ? labelSubmitting : labelSubmit}
          </IntuiButton>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Gated on the uiv2 flag (#7537): prod keeps the existing modal until the
// flag flips; the V2 Figma restyle only renders when useUIv2() is true.
export const BugReportModal = (props: BugReportModalProps) => {
  const isV2 = useUIv2()
  return isV2 ? <BugReportModalV2 {...props} /> : <BugReportModalLegacy {...props} />
}

export default BugReportModal
